import { useRef } from "react";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useDialogFocus } from "@/hooks/useDialogFocus";

export type CaixaFilterDraft = {
  client: string;
  store: string;
  from: string;
  to: string;
};

export const EMPTY_CAIXA_FILTERS: CaixaFilterDraft = {
  client: "",
  store: "",
  from: "",
  to: "",
};

interface CaixasFilterModalProps {
  draft: CaixaFilterDraft;
  onChange: (
    next: CaixaFilterDraft | ((current: CaixaFilterDraft) => CaixaFilterDraft),
  ) => void;
  onApply: () => void;
  onClear: () => void;
  onClose: () => void;
}

export function CaixasFilterModal({
  draft,
  onChange,
  onApply,
  onClear,
  onClose,
}: CaixasFilterModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const clientInputRef = useRef<HTMLInputElement>(null);
  useDialogFocus(panelRef, onClose, clientInputRef);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="caixas-filter-title"
      className="caixas-filter-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 240,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        background: "rgba(0,0,0,0.46)",
        backdropFilter: "blur(6px)",
      }}
    >
      <div
        ref={panelRef}
        className="caixas-filter-panel"
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(100%, 520px)",
          maxHeight: "calc(100dvh - 2rem)",
          overflow: "auto",
          borderRadius: "1.2rem",
          border: "1px solid var(--login-border)",
          background: "var(--login-surface)",
          boxShadow: "0 24px 80px rgba(0,0,0,0.24)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "0.75rem",
            padding: "1rem",
            borderBottom: "1px solid var(--login-border)",
          }}
        >
          <div>
            <div
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 700,
                fontSize: "0.76rem",
                letterSpacing: "0.08em",
                color: "var(--login-muted)",
                textTransform: "uppercase",
              }}
            >
              Filtros de caixas
            </div>
            <h2
              id="caixas-filter-title"
              style={{
                margin: "0.35rem 0 0",
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 700,
                fontSize: "1.45rem",
                letterSpacing: "0.03em",
                color: "var(--login-text)",
              }}
            >
              Cliente, loja e período
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar filtros de caixas"
            style={{
              width: "44px",
              height: "44px",
              borderRadius: "0.85rem",
              border:
                "1px solid color-mix(in srgb, #ef4444 28%, var(--login-border))",
              background: "color-mix(in srgb, #ef4444 10%, var(--login-bg))",
              color: "#ef4444",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ display: "grid", gap: "0.85rem", padding: "1rem" }}>
          <Input
            ref={clientInputRef}
            aria-label="Cliente"
            placeholder="Cliente"
            spellCheck={false}
            autoCorrect="off"
            value={draft.client}
            onInput={(event) => {
              const value = event.currentTarget.value;
              onChange((current) => ({
                ...current,
                client: value,
              }));
            }}
            className="caixas-filter-input h-11 rounded-xl"
          />
          <Input
            aria-label="Loja"
            placeholder="Loja"
            spellCheck={false}
            autoCorrect="off"
            value={draft.store}
            onInput={(event) => {
              const value = event.currentTarget.value;
              onChange((current) => ({
                ...current,
                store: value,
              }));
            }}
            className="caixas-filter-input h-11 rounded-xl"
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: "0.85rem",
            }}
          >
            <Input
              aria-label="De"
              placeholder="De"
              type="date"
              value={draft.from}
              onInput={(event) => {
                const value = event.currentTarget.value;
                onChange((current) => ({
                  ...current,
                  from: value,
                }));
              }}
              className="caixas-filter-input h-11 rounded-xl"
            />
            <Input
              aria-label="Até"
              placeholder="Até"
              type="date"
              value={draft.to}
              onInput={(event) => {
                const value = event.currentTarget.value;
                onChange((current) => ({
                  ...current,
                  to: value,
                }));
              }}
              className="caixas-filter-input h-11 rounded-xl"
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0 1rem 1rem",
          }}
        >
          <button
            type="button"
            className="caixas-pill-button caixas-pill-button-secondary"
            onClick={onClear}
          >
            Limpar
          </button>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              type="button"
              className="caixas-pill-button caixas-pill-button-secondary"
              onClick={onClose}
            >
              Fechar
            </button>
            <button
              type="button"
              className="caixas-pill-button caixas-pill-button-primary"
              onClick={onApply}
            >
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
